import { useCallback, useRef, useState } from 'react';
import type { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';

// ── Node / Link data shapes ──────────────────────────────────────────────────

export interface GraphNode {
  id: string;
  name: string;
  label: string;
  color: string;
  size: number;
  isSeed: boolean;
  pinned?: boolean;
  age?: number;         // query-age for subgraph panel fading
  strategy?: string;
  score?: number;
  fx?: number;
  fy?: number;
}

export interface GraphLink {
  source: string;
  target: string;
  rel_type: string;
  color: string;
  particleColor: string;
  expanded?: boolean;
  data_source?: string;
}

export type FGNode = NodeObject<GraphNode>;
export type FGLink = LinkObject<GraphNode, GraphLink>;
export type FGRef = ForceGraphMethods<GraphNode, GraphLink>;

// ── Color maps ───────────────────────────────────────────────────────────────

export { NODE_COLORS, nodeColor } from '../../lib/entityColors';

const EDGE_COLORS: Record<string, { line: string; particle: string }> = {
  USES:                { line: 'rgba(197, 108, 240, 0.20)', particle: 'rgba(197, 108, 240, 0.7)' },
  IDENTIFIES:          { line: 'rgba(255, 159, 67, 0.20)',  particle: 'rgba(255, 159, 67, 0.7)' },
  REFERENCED_IN:       { line: 'rgba(46, 213, 115, 0.15)',  particle: 'rgba(46, 213, 115, 0.6)' },
  MENTIONS:            { line: 'rgba(46, 213, 115, 0.15)',  particle: 'rgba(46, 213, 115, 0.6)' },
  USES_TECHNIQUE:      { line: 'rgba(255, 107, 129, 0.20)', particle: 'rgba(255, 107, 129, 0.7)' },
  SUBTECHNIQUE_OF:     { line: 'rgba(255, 71, 87, 0.20)',   particle: 'rgba(255, 71, 87, 0.7)' },
  REFERENCES_TECHNIQUE:{ line: 'rgba(254, 202, 87, 0.18)',  particle: 'rgba(254, 202, 87, 0.7)' },
  SAME_AS:             { line: 'rgba(119, 140, 163, 0.15)', particle: 'rgba(119, 140, 163, 0.6)' },
  PART_OF:             { line: 'rgba(119, 140, 163, 0.15)', particle: 'rgba(119, 140, 163, 0.6)' },
  CO_REFERENCED:       { line: 'rgba(77, 171, 247, 0.18)',  particle: 'rgba(77, 171, 247, 0.6)' },
  CO_OCCURS:           { line: 'rgba(254, 202, 87, 0.18)',  particle: 'rgba(254, 202, 87, 0.6)' },
};

const DEFAULT_EDGE = { line: 'rgba(255, 255, 255, 0.08)', particle: 'rgba(255, 255, 255, 0.4)' };

export { EDGE_COLORS };

export function edgeColor(relType: string) {
  return EDGE_COLORS[relType] ?? DEFAULT_EDGE;
}

// ── Knowledge source (provenance) coloring ──────────────────────────────────

export type KnowledgeSource =
  | 'malpedia'
  | 'mitre_attack'
  | 'blog_knowledge'
  | 'enrichment'
  | 'computed';

export const KNOWLEDGE_SOURCE_LABELS: Record<KnowledgeSource, string> = {
  malpedia:       'Malpedia',
  mitre_attack:   'MITRE ATT&CK',
  blog_knowledge: 'Blog Knowledge',
  enrichment:     'Enrichment',
  computed:       'Computed',
};

export const SOURCE_COLORS: Record<KnowledgeSource, { line: string; particle: string; swatch: string }> = {
  malpedia:       { line: 'rgba(46, 213, 115, 0.22)',  particle: 'rgba(46, 213, 115, 0.7)',  swatch: '#2ed573' },
  mitre_attack:   { line: 'rgba(255, 107, 129, 0.22)', particle: 'rgba(255, 107, 129, 0.7)', swatch: '#ff6b81' },
  blog_knowledge: { line: 'rgba(77, 171, 247, 0.22)',  particle: 'rgba(77, 171, 247, 0.7)',  swatch: '#4dabf7' },
  enrichment:     { line: 'rgba(197, 108, 240, 0.22)', particle: 'rgba(197, 108, 240, 0.7)', swatch: '#c56cf0' },
  computed:       { line: 'rgba(119, 140, 163, 0.15)', particle: 'rgba(119, 140, 163, 0.6)', swatch: '#778ca3' },
};

const REL_TYPE_TO_SOURCE: Record<string, KnowledgeSource> = {
  USES:                 'malpedia',
  PART_OF:              'mitre_attack',
  SUBTECHNIQUE_OF:      'mitre_attack',
  USES_TECHNIQUE:       'mitre_attack',
  USES_SOFTWARE:        'mitre_attack',
  MITIGATES:            'mitre_attack',
  REFERENCES_TECHNIQUE: 'mitre_attack',
  SAME_AS:              'mitre_attack',
  MENTIONS:             'blog_knowledge',
  AFFECTS:              'enrichment',
  CLASSIFIED_AS:        'enrichment',
  RESOLVED_TO:          'enrichment',
  IDENTIFIES:           'enrichment',
  CO_REFERENCED:        'computed',
  CO_OCCURS:            'computed',
};

export function resolveKnowledgeSource(relType: string, dataSource?: string): KnowledgeSource {
  // REFERENCED_IN is ambiguous — use data_source to disambiguate
  if (relType === 'REFERENCED_IN') {
    if (dataSource === 'malpedia') return 'malpedia';
    return 'blog_knowledge';
  }
  return REL_TYPE_TO_SOURCE[relType] ?? 'computed';
}

export function sourceEdgeColor(relType: string, dataSource?: string) {
  const ks = resolveKnowledgeSource(relType, dataSource);
  return SOURCE_COLORS[ks];
}

export type EdgeColorMode = 'source' | 'rel_type';

// ── Label shortening ────────────────────────────────────────────────────────

export function shortenLabel(name: string, maxLen = 30): string {
  if (name.length <= maxLen) return name;

  // URL shortening: extract hostname + truncated path
  if (name.startsWith('http://') || name.startsWith('https://')) {
    try {
      const url = new URL(name);
      let host = url.hostname.replace(/^www\./, '');
      if (host.length >= maxLen) return host.slice(0, maxLen - 1) + '\u2026';
      const remaining = maxLen - host.length - 1; // -1 for the /
      if (url.pathname.length > 1 && remaining > 3) {
        const path = url.pathname.slice(1); // strip leading /
        return host + '/' + path.slice(0, remaining - 1) + '\u2026';
      }
      return host;
    } catch {
      // fall through to generic truncation
    }
  }

  return name.slice(0, maxLen - 1) + '\u2026';
}

// ── Hex → rgba helper ────────────────────────────────────────────────────────

function hexToRgba(hex: string, alpha: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Hook options ─────────────────────────────────────────────────────────────

export interface UseForceGraphOptions {
  onNodeClick?: (node: FGNode, event: MouseEvent) => void;
  onNodeDoubleClick?: (node: FGNode) => void;
  onNodeRightClick?: (node: FGNode, event: MouseEvent) => void;
  onNodeDragEnd?: (node: FGNode) => void;
  onBackgroundClick?: () => void;
  onBackgroundRightClick?: (event: MouseEvent) => void;
  highlightedNodeId?: string | null;
  seedNodeId?: string | null;
}

// ── The hook ─────────────────────────────────────────────────────────────────

export function useForceGraph(opts: UseForceGraphOptions) {
  const fgRef = useRef<FGRef | undefined>(undefined);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [highlightSet, setHighlightSet] = useState<Set<string>>(new Set());

  // Last-click timer for double-click detection
  const lastClickRef = useRef<{ nodeId: string; time: number } | null>(null);

  // ── Node canvas rendering ──────────────────────────────────────────────

  const nodeCanvasObject = useCallback((node: FGNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
    const x = node.x ?? 0;
    const y = node.y ?? 0;
    const size = (node as GraphNode).size ?? 5;
    const color = (node as GraphNode).color ?? '#aaa';
    const isSeed = (node as GraphNode).isSeed;
    const isPinned = (node as GraphNode).pinned;
    const age = (node as GraphNode).age ?? 0;
    const isHovered = hoveredNodeId === node.id;
    const isHighlighted = highlightSet.size > 0 && highlightSet.has(node.id as string);
    const isDimmed = highlightSet.size > 0 && !highlightSet.has(node.id as string);
    const isSelected = opts.highlightedNodeId === node.id;

    // Age-based opacity reduction
    const ageAlpha = age > 2 ? 0.5 : 1.0;

    ctx.save();

    if (isDimmed) {
      ctx.globalAlpha = 0.08 * ageAlpha;
    } else {
      ctx.globalAlpha = ageAlpha;
    }

    // Glow
    let blur = isSeed ? 22 : 14;
    if (isHovered || isSelected) blur = 28;
    ctx.shadowColor = hexToRgba(color, 0.6);
    ctx.shadowBlur = blur;

    // Seed node pulse: oscillate shadowBlur slightly
    if (isSeed) {
      const pulse = Math.sin(Date.now() / 400) * 4;
      ctx.shadowBlur = 22 + pulse;
    }

    // Draw filled circle with radial gradient
    const grad = ctx.createRadialGradient(x, y, 0, x, y, size);
    grad.addColorStop(0, hexToRgba(color, 1.0));
    grad.addColorStop(0.7, hexToRgba(color, 0.85));
    grad.addColorStop(1, hexToRgba(color, 0.6));

    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fillStyle = grad;
    ctx.fill();

    // Remove shadow for subsequent drawing
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';

    // Pinned indicator: gold ring
    if (isPinned) {
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, size + 2, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Selected indicator: white ring
    if (isSelected && !isPinned) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(x, y, size + 1.5, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Label — show when zoomed in enough, or for seed/hovered nodes
    const showLabel = isSeed || isHovered || isSelected || globalScale > 1.2;
    if (showLabel && !isDimmed) {
      const rawName = (node as GraphNode).name ?? '';
      const displayName = shortenLabel(rawName, isSeed ? 35 : 25);
      const fontSize = isSeed ? 4.5 : 3.5;
      ctx.font = `${fontSize}px 'JetBrains Mono', 'Fira Code', monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Dark outline for legibility
      ctx.strokeStyle = '#0a0a0f';
      ctx.lineWidth = 2.5;
      ctx.lineJoin = 'round';
      ctx.strokeText(displayName, x, y + size + 2);

      // White fill
      ctx.fillStyle = isHovered || isSelected ? '#ffffff' : '#e0e6ed';
      ctx.fillText(displayName, x, y + size + 2);
    }

    ctx.restore();
  }, [hoveredNodeId, highlightSet, opts.highlightedNodeId]);

  // ── Pointer area (so hover/click detection uses the circle shape) ──────

  const nodePointerAreaPaint = useCallback((node: FGNode, paintColor: string, ctx: CanvasRenderingContext2D) => {
    const x = node.x ?? 0;
    const y = node.y ?? 0;
    const size = (node as GraphNode).size ?? 5;
    ctx.beginPath();
    ctx.arc(x, y, size + 2, 0, 2 * Math.PI);
    ctx.fillStyle = paintColor;
    ctx.fill();
  }, []);

  // ── Hover handling ─────────────────────────────────────────────────────

  const handleNodeHover = useCallback((node: FGNode | null, _prev: FGNode | null, graphData?: { nodes: FGNode[]; links: FGLink[] }) => {
    setHoveredNodeId(node ? (node.id as string) : null);

    if (!node || !graphData) {
      setHighlightSet(new Set());
      return;
    }

    // Build neighborhood set
    const nid = node.id as string;
    const neighbors = new Set<string>([nid]);
    for (const link of graphData.links) {
      const src = typeof link.source === 'object' ? (link.source as FGNode).id as string : String(link.source);
      const tgt = typeof link.target === 'object' ? (link.target as FGNode).id as string : String(link.target);
      if (src === nid) neighbors.add(tgt);
      if (tgt === nid) neighbors.add(src);
    }
    setHighlightSet(neighbors);
  }, []);

  const handleNodeUnhover = useCallback(() => {
    setHoveredNodeId(null);
    setHighlightSet(new Set());
  }, []);

  // ── Click with double-click detection ──────────────────────────────────

  const handleNodeClick = useCallback((node: FGNode, event: MouseEvent) => {
    const now = Date.now();
    const last = lastClickRef.current;
    if (last && last.nodeId === node.id && now - last.time < 350) {
      // Double click
      lastClickRef.current = null;
      opts.onNodeDoubleClick?.(node);
    } else {
      lastClickRef.current = { nodeId: node.id as string, time: now };
      opts.onNodeClick?.(node, event);
    }
  }, [opts.onNodeClick, opts.onNodeDoubleClick]);

  // ── Link styling helpers ───────────────────────────────────────────────

  const linkColor = useCallback((link: FGLink) => {
    const gl = link as GraphLink;
    const baseColor = gl.color ?? edgeColor(gl.rel_type ?? '').line;
    // If we have a highlight set, dim non-neighboring edges
    if (highlightSet.size > 0) {
      const src = typeof link.source === 'object' ? (link.source as FGNode).id as string : String(link.source);
      const tgt = typeof link.target === 'object' ? (link.target as FGNode).id as string : String(link.target);
      if (!highlightSet.has(src) || !highlightSet.has(tgt)) {
        return 'rgba(255, 255, 255, 0.02)';
      }
      // Highlighted edge — brighter
      return baseColor.replace(/[\d.]+\)$/, '0.5)');
    }
    return baseColor;
  }, [highlightSet]);

  const linkWidth = useCallback((link: FGLink) => {
    if (highlightSet.size > 0) {
      const src = typeof link.source === 'object' ? (link.source as FGNode).id as string : String(link.source);
      const tgt = typeof link.target === 'object' ? (link.target as FGNode).id as string : String(link.target);
      if (highlightSet.has(src) && highlightSet.has(tgt)) return 1.5;
    }
    return (link as GraphLink).expanded ? 0.4 : 0.7;
  }, [highlightSet]);

  const linkParticleColor = useCallback((link: FGLink) => {
    return (link as GraphLink).particleColor ?? 'rgba(255, 255, 255, 0.4)';
  }, []);

  const linkLineDash = useCallback((link: FGLink) => {
    return (link as GraphLink).expanded ? [2, 2] : null;
  }, []);

  // ── Focus / zoom helpers ───────────────────────────────────────────────

  const focusNode = useCallback((nodeId: string, graphData: { nodes: FGNode[] }) => {
    const fg = fgRef.current;
    if (!fg) return;
    const node = graphData.nodes.find(n => n.id === nodeId);
    if (node && node.x != null && node.y != null) {
      fg.centerAt(node.x, node.y, 300);
      fg.zoom(2.5, 300);
    }
  }, []);

  const zoomToFit = useCallback((padding = 40, duration = 400) => {
    fgRef.current?.zoomToFit(duration, padding);
  }, []);

  return {
    fgRef,
    hoveredNodeId,
    highlightSet,
    nodeCanvasObject,
    nodePointerAreaPaint,
    handleNodeHover,
    handleNodeUnhover,
    handleNodeClick,
    linkColor,
    linkWidth,
    linkParticleColor,
    linkLineDash,
    focusNode,
    zoomToFit,
  };
}
